import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import Header from '@/components/custom/header/header';
import { Menu } from '@/components/custom/menu';
import CpfModal from 'src/app/components/CpfModal';
import { useSession } from 'src/app/hooks/useSession';
import PtsModal from '@/app/screens/users/PtsModal';
import ActionCableConsumer from 'src/app/utils/actionCableConsumer';
import DuelNotification from 'src/app/components/DuelNotification';
import NoClassroomModal from 'src/app/components/NoClassroomModal';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { LogosSeduc } from 'src/app/components/LogosSeduc/LogosSeduc';
import PersonalDataModal from '@/app/components/PersonalDataModal';
import CpfConfirmationModal from '../app/components/CpfConfirmationModal';
import { Dashboard } from '@/layouts/dashboard';

const pageLayoutsWrapperVariants = cva('c-page-layouts-wrapper w-full mt-0', {
  variants: {
    hasCustomLogos: {
      true: 'mt-[68px] md:mt-0',
      false: 'mt-0'
    }
  },
  defaultVariants: {
    hasCustomLogos: false
  }
});

const PageLayoutsWrapper = React.forwardRef(({ className, hasCustomLogos, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(pageLayoutsWrapperVariants({ hasCustomLogos }), className)}
      {...props}
    />
  );
});
PageLayoutsWrapper.displayName = 'PageLayoutsWrapper';

export function PageLayouts(props) {
  const { layoutType, children } = props;
  const [showModal, setShowModal] = React.useState(false);
  const { session } = useSession();
  const [achievementData, setAchievementData] = React.useState(null);
  const [duelData, setDuelData] = React.useState(null);
  const [showPersonalDataModal, setShowPersonalDataModal] = React.useState(false);
  const [showCpfConfirmationModal, setShowCpfConfirmationModal] = React.useState(false);

  const reviwedData = session?.user?.reviewedInfos;

  //noclassroom modal
  const client = useClient();
  const currentUser = session?.user;
  const [show, setShow] = React.useState(false);
  const [studentClassrooms, setStudentClassrooms] = React.useState();
  const [verifiedUser, setVerifiedUser] = React.useState(null);

  const renderCPF = currentUser?.cpfCnpj === null || currentUser?.cpfCnpj === '';
  const renderClassrooms = studentClassrooms?.length === 0 || studentClassrooms?.some(e => ['turma única', 'sem turma', 'sem escola'].includes(e.title.toLowerCase()) && !['sem escola', 'já sou formado'].includes(e.institution.name.toLowerCase()));

  const appName = process.env.REACT_APP_NAME;
  const allowCreateUser = process.env.REACT_APP_ALLOW_CREATE_USER === 'true';
  const hasCustomLogos = appName === 'canaleducacao' || appName === 'maiseducacao';
  const isMainLayout = layoutType === 'mainLayout';

  React.useEffect(() => {
    const openModal = !reviwedData && currentUser?.profileNames[0] === 'Aluno' && (renderCPF || renderClassrooms) && !allowCreateUser;
    setShow(openModal);
  }, [studentClassrooms, currentUser, allowCreateUser]);

  async function getStudentClassroom() {
    const { data, error } = await client.fetch('classrooms');
    if (error) {
      toast.error('Erro ao buscar turmas');
    } else {
      setStudentClassrooms(data);
    }
  }

  async function checkVerifiedUser() {
    const { data, error } = await client.fetch(`users/${currentUser.id}/verifications`);
    if (error) {
      toast.error('Erro ao validar dados do usuário');
    } else {
      setVerifiedUser(data);
    }
  }

  React.useEffect(() => {
    if (session) {
      if (currentUser?.profileNames[0] === 'Aluno' || currentUser?.profileNames[0] === 'Professor') {
        checkVerifiedUser();
      }
      if (currentUser?.profileNames[0] === 'Aluno') {
        getStudentClassroom();
      }
    }
  }, [session]);

  React.useEffect(() => {
    if (appName === 'maiseducacao' && currentUser?.profileNames[0] === 'Aluno' && verifiedUser?.['cpf-verified'] === false) {
      setShowCpfConfirmationModal(true);
    }

    if (currentUser?.profileNames[0] === 'Aluno' && verifiedUser?.['cpf-verified'] === false && appName === 'canaleducacao') {
      setShowPersonalDataModal(true);
    }
  }, [verifiedUser]);

  return (
    <Dashboard>
      {isMainLayout && session && (
        <>
          <LogosSeduc hasCustomLogos={hasCustomLogos} />
          <Menu hasCustomLogos={hasCustomLogos} />
          <Header hasCustomLogos={hasCustomLogos} />
          <NoClassroomModal
            renderCPF={renderCPF}
            renderClassrooms={renderClassrooms}
            show={show}
            setShow={setShow}
            currentUser={currentUser}
          />
          {showPersonalDataModal && (
            <PersonalDataModal
              show={showPersonalDataModal}
              setShow={setShowPersonalDataModal}
              currentUser={currentUser}
              verifiedUser={verifiedUser}
            />
          )}
          <CpfConfirmationModal
            showModal={showCpfConfirmationModal}
            setShowModal={setShowCpfConfirmationModal}
            currentUser={currentUser}
          />
          <CpfModal
            showModal={showModal}
            setShowModal={setShowModal}
          />
          <PtsModal
            achievementData={achievementData}
            setAchievementData={setAchievementData}
          />
          <DuelNotification
            duelData={duelData}
            setDuelData={setDuelData}
          />
          <ActionCableConsumer
            channel="UserMilestoneChannel"
            setObjectChannel={setAchievementData}
            channelKey={'user_id'}
            channelId={session?.user?.id}
          />
          <ActionCableConsumer
            channel="DuelNotificationsChannel"
            setObjectChannel={setDuelData}
            token={session.user.authToken}
          />
        </>
      )}

      <PageLayoutsWrapper hasCustomLogos={hasCustomLogos && isMainLayout}>{children}</PageLayoutsWrapper>
    </Dashboard>
  );
}
